import React from 'react';
import { Box, SimpleGrid, Text, Flex, HStack, Icon, VStack, Spinner } from '@chakra-ui/react';
import { FiCalendar, FiArrowRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const STATUS_BADGE = {
  pending: { label: 'Needs review', color: 'rgba(251,191,36,0.95)', bg: 'rgba(251,191,36,0.1)', border: 'rgba(251,191,36,0.28)' },
  approved: { label: 'Approved', color: 'rgba(122,226,161,0.95)', bg: 'rgba(122,226,161,0.1)', border: 'rgba(122,226,161,0.28)' },
  rejected: { label: 'Rejected', color: 'rgba(248,113,113,0.95)', bg: 'rgba(248,113,113,0.1)', border: 'rgba(248,113,113,0.28)' },
  changes_requested: { label: 'Changes requested', color: 'rgba(251,191,36,0.95)', bg: 'rgba(251,191,36,0.1)', border: 'rgba(251,191,36,0.28)' },
};

const ReviewerEventCard = ({ share }) => {
  const navigate = useNavigate();
  const badge = STATUS_BADGE[share.compliance_status] || STATUS_BADGE.pending;

  return (
    <Box
      bg="#0f0f0f"
      borderRadius="12px"
      border="1px solid rgba(255,255,255,0.10)"
      p={4}
      cursor="pointer"
      role="group"
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-2px)', borderColor: 'rgba(255,255,255,0.22)' }}
      onClick={() => navigate(`/events/review/${share.event_id}`)}
    >
      <Flex align="center" justify="space-between" mb={3}>
        <Box
          px={2}
          py="2px"
          borderRadius="full"
          bg={badge.bg}
          border={`1px solid ${badge.border}`}
        >
          <Text fontSize="10px" fontWeight="600" color={badge.color} letterSpacing="0.06em" textTransform="uppercase">
            {badge.label}
          </Text>
        </Box>
        <Icon as={FiArrowRight} color="rgba(255,255,255,0.3)" _groupHover={{ color: '#fafafa' }} />
      </Flex>

      <Text fontSize="md" fontWeight="600" color="#fafafa" noOfLines={2} lineHeight="1.4" mb={2}>
        {share.event_title || 'Untitled event'}
      </Text>

      <HStack spacing={2} color="#a3a3a3" fontSize="xs">
        <FiCalendar size={12} />
        <Text noOfLines={1}>
          Shared by {share.owner_display_name || 'an advisor'}
        </Text>
      </HStack>
    </Box>
  );
};

const ReviewerEventList = ({ shares, isLoading, error }) => {
  if (isLoading) {
    return (
      <Flex align="center" justify="center" py={10}>
        <Spinner size="md" color="rgba(255,255,255,0.4)" thickness="2px" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={4} bg="#171717" color="rgba(248,113,113,0.9)" borderRadius="md" border="1px solid rgba(248,113,113,0.3)">
        <Text>Error loading shared events: {error.message}</Text>
      </Box>
    );
  }

  if (!shares || shares.length === 0) {
    return (
      <Box py={10} textAlign="center">
        <VStack spacing={3}>
          <Icon as={FiCalendar} boxSize={10} color="rgba(255,255,255,0.25)" strokeWidth={1} />
          <Box>
            <Text fontSize="md" fontWeight="600" color="#fafafa">Nothing shared with you yet</Text>
            <Text fontSize="sm" color="#a3a3a3" mt={1}>
              When someone invites you to review one of their events, it'll show up here.
            </Text>
          </Box>
        </VStack>
      </Box>
    );
  }

  return (
    <SimpleGrid columns={[1, null, 2, 3]} spacing={4} px={1}>
      {shares.map((share) => (
        <ReviewerEventCard key={share.share_id} share={share} />
      ))}
    </SimpleGrid>
  );
};

export default ReviewerEventList;
